import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {catchError, Observable} from 'rxjs';
import {
  IErpOutboundQueueMsgResponse,
  ISalesOrderSyncQueueItem,
  IUpdateSalesOrderErpPropsRequest,
} from '../interfaces/tmt-erp-panel.interface';

/**
 * Lets a user manually close out the TMT -> ERP send for a sales order when the real ERP
 * outbound pipeline can't run (e.g. non-prod environments where ERP outbound is disabled).
 * Mirrors the two calls fms-erp-api/finance-ledger make automatically after a real send:
 * stamping the sync-queue row(s) with an ERP message id/timestamp, then recording the ERP
 * response against that same queue row.
 */
@Injectable({
  providedIn: 'root',
})
export class TmtErpManualUpdateService {
  private readonly http = inject(HttpClient);

  getPendingSyncQueue(salesOrderId: number): Observable<ISalesOrderSyncQueueItem[]> {
    return this.http.get<ISalesOrderSyncQueueItem[]>('tmt/sales-order-sync-queues', {
      params: {salesOrderId, isSynced: false, isDeleted: false},
    }).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.error?.detail || error?.message || 'Server error');
    }));
  }

  updateErpProps(payload: IUpdateSalesOrderErpPropsRequest): Observable<void> {
    return this.http.put<void>('tmt/sales-orders/update-sales-order-erp-props', payload)
      .pipe(catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.detail || error?.message || 'Server error');
      }));
  }

  upsertErpResponse(payload: IErpOutboundQueueMsgResponse): Observable<void> {
    return this.http.put<void>('tmt/sales-order-erp-responses', payload)
      .pipe(catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.detail || error?.message || 'Server error');
      }));
  }

  /**
   * Soft-deletes an existing SalesOrderErpResponse row. Used to let a user correct a mistake in
   * a previous manual (or real) entry: Upsert only truly overwrites an existing row when its
   * Result is still "PENDING" — once it's SUCCESS/ERROR, resubmitting with the same ErpMessageId
   * would insert a second row instead of fixing the first. Deleting the old row first, then
   * saving again, guarantees a clean edit regardless of its prior Result.
   */
  deleteErpResponse(salesOrderErpResponseId: number): Observable<void> {
    return this.http.delete<void>('tmt/sales-order-erp-responses', {
      params: {SalesOrderErpResponseId: salesOrderErpResponseId},
    }).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.error?.detail || error?.message || 'Server error');
    }));
  }
}
